import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import customersService, { Customer } from '../services/clientsService';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search,
  User,
  Building2,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Mail,
  Phone,
  ChevronLeft,
} from 'lucide-react';
import LoadingScreen from '../components/ui/LoadingScreen';

const ITEMS_PER_PAGE = 12;

export default function CustomerPaymentSelection() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [page, setPage] = useState(1);

    useEffect(() => {
        fetchCustomers();
    }, []);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, typeFilter]);

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const data = await customersService.getAllCustomers();
            setCustomers(data.filter(c => c.isActive));
            setError(null);
        } catch (err) {
            setError('Error al cargar los clientes');
            console.error('Error fetching customers:', err);
        } finally {
            setLoading(false);
        }
    };

    const getCustomerName = (customer: Customer): string => {
        if (customer.type === 'PERSON') return `${customer.firstName} ${customer.lastName}`;
        return customer.businessName;
    };

    const getCustomerEmail = (customer: Customer): string => {
        if (customer.type === 'PERSON') return customer.email || '';
        return customer.contactEmail || customer.email || '';
    };

    const filteredCustomers = customers.filter(customer => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            getCustomerName(customer).toLowerCase().includes(term) ||
            getCustomerEmail(customer).toLowerCase().includes(term) ||
            customer.cuit.toLowerCase().includes(term);
        const matchesType =
            typeFilter === 'all' ||
            (typeFilter === 'PERSON' && customer.type === 'PERSON') ||
            (typeFilter === 'COMPANY' && customer.type === 'COMPANY');
        return matchesSearch && matchesType;
    });

    const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
    const safePage = Math.min(page, Math.max(1, totalPages));
    const pageCustomers = filteredCustomers.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

    if (loading) return <LoadingScreen message="Cargando clientes..." />;

    return (
        <div className="w-full h-full p-4 sm:p-6">
            <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

            {/* ── Header ── */}
            <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-card to-muted/30">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Cobranzas</h1>
                        <p className="text-sm text-muted-foreground">
                            Seleccioná un cliente para ver sus cobranzas
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                        <User className="w-3.5 h-3.5" />
                        {customers.length} cliente{customers.length !== 1 ? 's' : ''}
                    </span>
                    <Button variant="outline" size="sm" onClick={fetchCustomers} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </Button>
                </div>
            </div>
            </div>{/* end header px-6 py-5 */}

            <div className="p-6 space-y-6">

            {/* ── Error ── */}
            {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-destructive">{error}</p>
                        <button onClick={fetchCustomers} className="text-xs text-destructive/70 hover:text-destructive mt-0.5 underline">
                            Reintentar
                        </button>
                    </div>
                </div>
            )}

            {/* ── Search + Filtros ── */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                    <Input
                        type="text"
                        placeholder="Nombre, email o CUIT..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 h-9"
                    />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-full sm:w-[180px] h-9 rounded-lg">
                        <SelectValue placeholder="Todos los tipos" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los tipos</SelectItem>
                        <SelectItem value="PERSON">Persona Física</SelectItem>
                        <SelectItem value="COMPANY">Empresa</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* ── Card Grid ── */}
            {filteredCustomers.length === 0 ? (
                <div className="bg-card border border-border rounded-xl flex flex-col items-center justify-center py-20 text-center">
                    <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-muted/50">
                        {searchTerm || typeFilter !== 'all'
                            ? <Search className="w-8 h-8 text-muted-foreground/40" />
                            : <User className="w-8 h-8 text-muted-foreground/40" />
                        }
                    </div>
                    <p className="text-base font-medium text-muted-foreground">
                        {searchTerm || typeFilter !== 'all' ? 'No se encontraron clientes' : 'No hay clientes activos'}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground/70">
                        {searchTerm || typeFilter !== 'all'
                            ? 'Probá con otros términos o cambiá el filtro'
                            : 'Los clientes aparecerán aquí cuando estén dados de alta'
                        }
                    </p>
                    {(searchTerm || typeFilter !== 'all') && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSearchTerm(''); setTypeFilter('all'); }}
                            className="mt-4"
                        >
                            Limpiar búsqueda
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {pageCustomers.map((customer) => {
                            const isPerson = customer.type === 'PERSON';
                            const email = getCustomerEmail(customer);
                            const phone = isPerson ? customer.phone : customer.contactPhone || customer.phone;
                            const contactName = !isPerson ? customer.contactName : null;

                            return (
                                <button
                                    key={customer.id}
                                    onClick={() => navigate(`/customer-payments/${customer.id}`)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/customer-payments/${customer.id}`);
                                        }
                                    }}
                                    className="flex flex-col bg-card border border-border rounded-xl overflow-hidden transition-shadow duration-200 hover:shadow-md text-left w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    {/* Card Header */}
                                    <div className="flex items-center gap-3 px-4 pt-4 pb-3 min-w-0">
                                        <div className={`flex-shrink-0 p-2.5 rounded-xl ${
                                            isPerson ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                                        }`}>
                                            {isPerson
                                                ? <User className="w-5 h-5" />
                                                : <Building2 className="w-5 h-5" />
                                            }
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-foreground truncate leading-tight">
                                                {getCustomerName(customer)}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {isPerson ? 'Persona Física' : 'Empresa'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="mx-4 border-t border-border" />

                                    {/* Info rows */}
                                    <div className="px-4 py-3 space-y-2 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-muted-foreground w-10 flex-shrink-0">CUIT</span>
                                            <span className="font-mono text-xs font-semibold text-foreground tracking-wide truncate">
                                                {customer.cuit}
                                            </span>
                                        </div>
                                        {email && (
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                                <span className="text-xs text-foreground truncate">{email}</span>
                                            </div>
                                        )}
                                        {phone && !email && (
                                            <div className="flex items-center gap-2">
                                                <Phone className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                                <span className="text-xs text-foreground truncate">{phone}</span>
                                            </div>
                                        )}
                                        {contactName && (
                                            <div className="flex items-center gap-2">
                                                <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                                <span className="text-xs text-muted-foreground truncate">{contactName}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* CTA */}
                                    <div className="mt-auto mx-4 border-t border-border" />
                                    <div className="px-4 py-3 flex items-center justify-between text-xs font-medium text-primary group-hover:text-primary">
                                        <span>Ver cobranzas</span>
                                        <ChevronRight className="w-4 h-4" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Paginación */}
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                            Mostrando{' '}
                            <span className="font-semibold text-foreground">{pageCustomers.length}</span> de{' '}
                            <span className="font-semibold text-foreground">{filteredCustomers.length}</span>{' '}
                            cliente{filteredCustomers.length !== 1 ? 's' : ''}
                        </p>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p - 1)}
                                    disabled={safePage === 1}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={safePage === totalPages}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}
            </div>{/* end content p-6 */}
            </div>{/* end card wrapper */}
        </div>
    );
}
